import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import FileUpload from "./FileUpload";
import api from "../api";
import StorageIcon from "@mui/icons-material/Storage";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import axios from "axios";
import logger from './../utils/logger.js';
import { useAuth } from '../context/AuthContext';

const FileUploadTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  
  // For database statistics dialog
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsTabValue, setStatsTabValue] = useState(0);
  const [databaseStats, setDatabaseStats] = useState({
    general: {},
    tables: [],
    schemas: [],
    metrics: {}
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // This is needed to force a refresh of the main data grid after file upload
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleFileUpload = async (file, customFormData, importResponse) => {
    // If importResponse is provided, it means the import was done via preview
    if (importResponse) {
      setLoading(false);
      setFileUploadProgress(0);
      const message = importResponse.message || 'Data successfully imported';
      const importInfo = importResponse.new_records !== undefined 
        ? ` ${importResponse.new_records} nieuwe records toegevoegd, ${importResponse.updated_records} records bijgewerkt.`
        : '';
      const claimInfo = importResponse.claimed_count 
        ? ` ${importResponse.claimed_count} records geclaimed voor gebruiker.`
        : '';
      setSuccess(message + importInfo + claimInfo);
      setRefreshTrigger(prev => prev + 1);
      return;
    }

    if (!file) {
      setError('No file selected');
      return;
    }

    setLoading(true);
    setError(null);
    setFileUploadProgress(0);

    try {
      // Use the provided custom FormData if available, otherwise create a new one
      const formData = customFormData || new FormData();
      
      // If we're using a new FormData (not the custom one), add the file
      if (!customFormData) {
        formData.append('file', file);
      }

      // Create a specific API instance just for this file upload with a longer timeout
      const uploadApi = axios.create({
        baseURL: api.defaults.baseURL,
        timeout: 1200000, // 20 minute timeout for very large files
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      // Send the file to the server with the dedicated upload API instance
      const response = await uploadApi.post('/api/files/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setFileUploadProgress(percentCompleted);
          // Reset timeout on progress to prevent timeout during large uploads
          uploadApi.defaults.timeout = 1200000; // Reset the 20 minute timeout on each progress event
        },
      });

      // Process the response
      if (response.data && response.data.success) {
        const message = response.data.message || 'File uploaded successfully';
        const importInfo = response.data.new_records !== undefined 
          ? ` ${response.data.new_records} nieuwe records toegevoegd, ${response.data.updated_records} records bijgewerkt.`
          : '';
        const claimInfo = response.data.claimed_count 
          ? ` ${response.data.claimed_count} records geclaimed voor gebruiker.`
          : '';
        setSuccess(message + importInfo + claimInfo);
        // Trigger a refresh of the main data grid
        setRefreshTrigger(prev => prev + 1);
      } else {
        setError(response.data?.message || 'Unknown error occurred during file upload');
      }
    } catch (error) {
      logger.error('Error uploading file:', error);
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(`Upload failed: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        // The request was made but no response was received
        setError('Upload failed: No response received from server. The server might be down or the file might be too large.');
      } else {
        // Something happened in setting up the request that triggered an Error
        setError(`Upload failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
      setFileUploadProgress(0);
    }
  };

  const handleLoadFromDatabase = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Load data from PostgreSQL database
      const response = await api.post('/api/load-from-database', {
        method: 'sqlalchemy'
      });
      
      if (response.data.success) {
        logger.log("Database loading successful:", response.data);
        setSuccess(response.data.message || `Successfully loaded ${response.data.record_count} records from database`);
        
        // Trigger a refresh of the main data grid
        setRefreshTrigger(prev => prev + 1);
      } else {
        setError(response.data.message || "Failed to load data from database");
      }
    } catch (error) {
      logger.error("Error loading from database:", error);
      setError(error.message || "An error occurred while loading from database");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckCurrentData = async () => {
    try {
      setLoading(true);
      
      // Use the new admin database-info endpoint that's faster and more secure
      const response = await api.get('/api/admin/database-info');
      logger.log("Database info status:", response.data);
      
      if (response.data.success) {
        const data = response.data.current_data || {};
        if (data.loaded && data.record_count > 0) {
          setSuccess(`Data is already loaded: ${data.record_count} records in memory with ${data.claimed_records_count} claimed records.`);
        } else {
          setError(`No data currently loaded (${data.record_count || 0} records). Try using 'Load from Database' button.`);
        }
        
        // Show database metrics if available
        if (response.data.table_count) {
          setSuccess(prev => `${prev}\nDatabase has ${response.data.table_count} tables with ${response.data.total_rows || 0} total rows.`);
        }
      } else {
        setError(`Error checking data status: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      logger.error("Error checking current data:", error);
      
      // Fallback to the old debug endpoint if the admin endpoint fails
      try {
        const fallbackResponse = await api.get('/api/debug/current-data');
        if (fallbackResponse.data.has_data && fallbackResponse.data.record_count > 0) {
          setSuccess(`Data is already loaded: ${fallbackResponse.data.record_count} records with ${fallbackResponse.data.column_count} columns.`);
        } else {
          setError(`No data currently loaded (${fallbackResponse.data.record_count} records). Try using 'Load from Database' button.`);
        }
      } catch (fallbackError) {
        setError("Error checking current data status: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGetDatabaseStats = async () => {
    try {
      setStatsLoading(true);
      setError(null);
      
      // Try the admin endpoint first
      try {
        const response = await api.get('/api/admin/database-stats');
        
        if (response.data && response.data.success) {
          logger.log("Database statistics from admin endpoint:", response.data);
          
          // Organize the data into a structured format for display
          setDatabaseStats({
            general: {
              name: response.data.database_name || "PostgreSQL Database",
              version: response.data.pg_version || "Unknown",
              size: response.data.database_size || "Unknown",
              uptime: response.data.uptime || "Unknown",
              connection_count: response.data.active_connections || 0
            },
            tables: response.data.tables || [],
            schemas: response.data.schemas || [],
            metrics: {
              total_tables: response.data.total_tables || 0,
              total_rows: response.data.total_rows || 0,
              avg_table_size: response.data.avg_table_size || "0 MB",
              largest_table: response.data.largest_table || "None",
              total_indexes: response.data.total_indexes || 0
            }
          });
          
          // Open the dialog to display the statistics
          setStatsDialogOpen(true);
          return; // Success - exit the function
        }
      } catch (adminError) {
        logger.error("Error accessing admin endpoint:", adminError);
        // Continue to fallback
      }
      
      // Fall back to debug endpoint if admin endpoint failed
      logger.log("Falling back to debug endpoint for database stats");
      const fallbackResponse = await api.get('/api/debug/database-stats');
      
      if (fallbackResponse.data && fallbackResponse.data.success) {
        logger.log("Database statistics from debug endpoint:", fallbackResponse.data);
        
        // Process data the same way as with the admin endpoint
        setDatabaseStats({
          general: {
            name: fallbackResponse.data.database_name || "PostgreSQL Database",
            version: fallbackResponse.data.pg_version || "Unknown",
            size: fallbackResponse.data.database_size || "Unknown",
            uptime: fallbackResponse.data.uptime || "Unknown",
            connection_count: fallbackResponse.data.active_connections || 0
          },
          tables: fallbackResponse.data.tables || [],
          schemas: fallbackResponse.data.schemas || [],
          metrics: {
            total_tables: fallbackResponse.data.total_tables || 0,
            total_rows: fallbackResponse.data.total_rows || 0,
            avg_table_size: fallbackResponse.data.avg_table_size || "0 MB",
            largest_table: fallbackResponse.data.largest_table || "None",
            total_indexes: fallbackResponse.data.total_indexes || 0
          }
        });
        
        // Open the dialog to display the statistics
        setStatsDialogOpen(true);
      } else {
        // Even if we don't have full data, we might have basic information
        setDatabaseStats({
          general: {
            name: (fallbackResponse.data && fallbackResponse.data.database_name) || "PostgreSQL Database",
            version: (fallbackResponse.data && fallbackResponse.data.pg_version) || "Unknown",
            size: (fallbackResponse.data && fallbackResponse.data.database_size) || "Unknown",
            uptime: (fallbackResponse.data && fallbackResponse.data.uptime) || "Unknown",
            connection_count: (fallbackResponse.data && fallbackResponse.data.active_connections) || 0
          },
          tables: (fallbackResponse.data && fallbackResponse.data.tables) || [],
          schemas: (fallbackResponse.data && fallbackResponse.data.schemas) || [],
          metrics: {
            total_tables: 0,
            total_rows: 0,
            avg_table_size: "0 MB",
            largest_table: "None",
            total_indexes: 0
          }
        });
        
        // Still open the dialog - we'll show at least basic info
        setStatsDialogOpen(true);
        
        // Show a warning if appropriate
        if (fallbackResponse.data && fallbackResponse.data.message) {
          setError(fallbackResponse.data.message);
        } else {
          setError("Unable to retrieve complete database statistics");
        }
      }
    } catch (error) {
      logger.error("Error getting database statistics:", error);
      
      // Create a fallback database stats object with empty/default values
      setDatabaseStats({
        general: {
          name: "PostgreSQL Database",
          version: "Unknown",
          size: "Unknown",
          uptime: "Unknown",
          connection_count: 0
        },
        tables: [],
        schemas: [],
        metrics: {
          total_tables: 0,
          total_rows: 0,
          avg_table_size: "0 MB",
          largest_table: "None",
          total_indexes: 0
        }
      });
      
      // Still open the dialog even with error - we'll just show what we can
      setStatsDialogOpen(true);
      
      // Show the error message
      setError("Error retrieving database statistics: " + (error.response?.data?.message || error.message));
    } finally {
      setStatsLoading(false);
    }
  };

  // Function to handle tab changes in the stats dialog
  const handleStatsTabChange = (event, newValue) => {
    setStatsTabValue(newValue);
  };

  // Handle template download
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get backend URL from api instance
      const backendUrl = api.defaults.baseURL || 'http://localhost:5000';
      const downloadUrl = `${backendUrl}/api/files/download-template`;
      
      // Fetch the file with credentials
      const response = await fetch(downloadUrl, {
        method: 'GET',
        credentials: 'include', // Include cookies for authentication
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download template: ${response.status} ${response.statusText}`);
      }
      
      // Get the file as a blob
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Salinator_Company_Import_Template_filters.xlsx';
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Template downloaded successfully');
    } catch (err) {
      logger.error('Error downloading template:', err);
      setError(`Failed to download template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Whenever refreshTrigger changes, notify parent to reload data
  useEffect(() => {
    if (refreshTrigger > 0) {
      // We could optionally send an event to parent or use context
      // For now we'll just rely on the App to check data status periodically
      logger.log("Data refresh triggered");
    }
  }, [refreshTrigger]);

  // Render the content for database statistics tab panels
  const renderStatsTabContent = (tabIndex) => {
    switch (tabIndex) {
      case 0: // General Info
        return (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Database Name</TableCell>
                    <TableCell>{databaseStats.general.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>PostgreSQL Version</TableCell>
                    <TableCell>{databaseStats.general.version}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Database Size</TableCell>
                    <TableCell>{databaseStats.general.size}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Server Uptime</TableCell>
                    <TableCell>{databaseStats.general.uptime}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Active Connections</TableCell>
                    <TableCell>{databaseStats.general.connection_count}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Database Metrics</Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Total Tables</TableCell>
                    <TableCell>{databaseStats.metrics.total_tables}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Total Rows</TableCell>
                    <TableCell>{databaseStats.metrics.total_rows}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Average Table Size</TableCell>
                    <TableCell>{databaseStats.metrics.avg_table_size}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Largest Table</TableCell>
                    <TableCell>{databaseStats.metrics.largest_table}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Total Indexes</TableCell>
                    <TableCell>{databaseStats.metrics.total_indexes}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      
      case 1: // Tables
        return (
          <Box sx={{ p: 2 }}>
            {databaseStats.tables.length === 0 ? (
              <Alert severity="info">No tables found in the database</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Table Name</TableCell>
                      <TableCell align="right">Row Count</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell align="right">Indexes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {databaseStats.tables.map((table) => (
                      <TableRow key={table.name}>
                        <TableCell>{table.name}</TableCell>
                        <TableCell align="right">{table.row_count}</TableCell>
                        <TableCell align="right">{table.size}</TableCell>
                        <TableCell align="right">{table.index_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      
      case 2: // Schemas
        return (
          <Box sx={{ p: 2 }}>
            {databaseStats.schemas.length === 0 ? (
              <Alert severity="info">No schema information available</Alert>
            ) : (
              <Box>
                {databaseStats.schemas.map((schema) => (
                  <Accordion key={schema.name}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{schema.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" gutterBottom>Tables: {schema.table_count}</Typography>
                      {schema.tables && schema.tables.length > 0 ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Table</TableCell>
                                <TableCell>Columns</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {schema.tables.map((table) => (
                                <TableRow key={table.name}>
                                  <TableCell>{table.name}</TableCell>
                                  <TableCell>{table.column_count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No tables in this schema</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        );
      
      default:
        return <Box>No data to display</Box>;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Data Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, width: '100%' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3, width: '100%' }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={user?.is_admin ? 6 : 12}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <Typography variant="h6" gutterBottom>
              File Upload
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload a new data file (.xlsx, .xls, .csv)
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                fullWidth
              >
                Download Import Template
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Download the template file to ensure proper formatting
              </Typography>
            </Box>
            
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <FileUpload 
                onFileUpload={handleFileUpload} 
                disabled={loading} 
                loading={loading} 
              />
            </Box>
          </Paper>
        </Grid>

        {user?.is_admin && (
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Database Operations
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Manage database data and view statistics
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleLoadFromDatabase}
                  disabled={loading}
                  startIcon={<StorageIcon />}
                >
                  Load from Database
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                  Load records into memory from the PostgreSQL database
                </Typography>
                
                <Button
                  variant="outlined"
                  color="info"
                  fullWidth
                  onClick={handleGetDatabaseStats}
                  disabled={loading || statsLoading}
                  startIcon={<BarChartIcon />}
                >
                  Database Statistics
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                  View detailed information about database tables, schemas and metrics
                </Typography>
                
                <Button
                  variant="outlined"
                  color="info"
                  fullWidth
                  onClick={handleCheckCurrentData}
                  disabled={loading}
                  startIcon={<InfoIcon />}
                >
                  Check Current Data
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                  Verify if data is loaded in memory and check table counts
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {loading && (
        <Box sx={{ width: '100%', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            {fileUploadProgress > 0 ? `Uploading: ${fileUploadProgress}%` : "Processing..."}
          </Typography>
          <CircularProgress size={24} sx={{ display: 'block', mx: 'auto' }} />
        </Box>
      )}

      {/* Database Statistics Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          PostgreSQL Database Statistics
        </DialogTitle>
        <DialogContent>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Tabs 
                value={statsTabValue} 
                onChange={handleStatsTabChange}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab label="General Info" />
                <Tab label="Tables" />
                <Tab label="Schemas" />
              </Tabs>
              
              {renderStatsTabContent(statsTabValue)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)}>
            Close
          </Button>
          <Button 
            color="primary" 
            onClick={handleGetDatabaseStats}
            disabled={statsLoading}
            startIcon={statsLoading ? <CircularProgress size={20} /> : null}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileUploadTab; 